// src/pages/UserManagement/UserManagementComponent.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import UserInviteDialog from './UserInviteDialog';
import CreateUserDialog from './CreateUserDialog';
import EditUserDialog from './EditUserDialog';

// Interface for user data
interface UserData {
  id: string;
  email: string;
  displayName: string;
  photoURL: string | null;
  role: string;
  createdAt: any;
}

// Added interface for component props
interface UserManagementComponentProps {
  onInviteClick: () => void;
  refreshTrigger: number;
}

const UserManagementComponent: React.FC<UserManagementComponentProps> = ({ 
  onInviteClick,
  refreshTrigger 
}) => {
  const { userId } = useParams();
  const navigate = useNavigate();
  
  // State variables
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Dialog states
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [editUserData, setEditUserData] = useState<{userId: string; displayName: string; role: string} | null>(null);

  // Fetch users on component mount and when refreshTrigger changes
  useEffect(() => {
    fetchUsers();
  }, [refreshTrigger]);
  
  // Fetch users from Firestore
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch real users from Firestore
      const usersRef = collection(db, 'users');
      const q = query(usersRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const fetchedUsers: UserData[] = [];
      querySnapshot.forEach((doc) => {
        fetchedUsers.push({
          id: doc.id,
          ...doc.data()
        } as UserData);
      });
      
      setUsers(fetchedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  // Filter users based on selected tab
  const filteredUsers = users.filter(user => {
    if (tabValue === 0) return true; // All users
    if (tabValue === 1) return user.role === 'owner'; // Owner users
    if (tabValue === 2) return user.role === 'editor'; // Editor users
    return false;
  });
  
  // Handle user selection
  const handleUserSelect = (user: UserData) => {
    navigate(`/users/${user.id}`);
  };
  
  // Handle delete click
  const handleDeleteClick = (user: UserData, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };
  
  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    try {
      // Delete from Firestore
      await deleteDoc(doc(db, 'users', userToDelete.id));
      
      // Update local state
      setUsers(users.filter(user => user.id !== userToDelete.id));
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    }
  };

  // Handle edit user
  const handleEditUser = (user: UserData, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setEditUserData({
      userId: user.id,
      displayName: user.displayName || '',
      role: user.role
    });
  };
  
  // Render loading state
  if (loading && !users.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h1">
          User Management
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateUserDialogOpen(true)}
          >
            Create User
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PersonAddIcon />}
            onClick={onInviteClick}
          >
            Invite User
          </Button>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="All Users" />
          <Tab label="Owners" />
          <Tab label="Editors" />
        </Tabs>
        
        {loading && users.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={30} />
          </Box>
        )}
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow 
                  key={user.id}
                  hover
                  onClick={() => handleUserSelect(user)}
                  selected={userId === user.id}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar src={user.photoURL || undefined} sx={{ mr: 2 }}>
                        {user.displayName?.charAt(0) || user.email.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography>{user.displayName || 'Unnamed User'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Chip 
                      label={user.role} 
                      color={user.role === 'owner' ? 'primary' : user.role === 'editor' ? 'secondary' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {user.createdAt?.toDate ? 
                      user.createdAt.toDate().toLocaleDateString() : 
                      'Unknown'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={(e) => handleEditUser(user, e)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={(e) => handleDeleteClick(user, e)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              
              {filteredUsers.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Create User Dialog */}
      <CreateUserDialog 
        open={createUserDialogOpen} 
        onClose={(refreshData) => {
          setCreateUserDialogOpen(false);
          if (refreshData) fetchUsers();
        }} 
      />
      
      {/* Edit User Dialog */}
      <EditUserDialog
        open={editUserData !== null}
        userData={editUserData}
        onClose={(refreshData) => {
          setEditUserData(null);
          if (refreshData) fetchUsers();
        }}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {userToDelete?.displayName || userToDelete?.email}? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserManagementComponent;